/**
 * AdPreviewModal — full-size platform-faithful ad preview with inline pan/crop.
 *
 * Each format renders its own lightweight platform chrome containing a
 * PannableImage in the image slot. Users drag to reposition; clicking "Save
 * position" persists CropParams back to the concept via onAdjustmentSave.
 * No separate editor window is opened.
 *
 * Finished ads (concept.bakedCopy: Ad Studio or the owner's own, words in the image) are shown
 * whole and never cropped: each format shows its own size's file (the rendition's assetUrl).
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { GoogleSearchAdMockup } from '@/components/ads/platform-specific/GoogleSearchAdMockup';
import { cn } from '@/lib/utils';
import { conceptAssetUrl } from './conceptArt';
import { PannableImage } from './PannableImage';
import { PlatformLogo, type PlatformKey } from '@/components/icons/PlatformLogos';
import type { CampaignState, CreativeConcept } from './types';
import { getStrategyPlatforms } from './types';
import type { CropParams } from '@/components/media/mediaAdjustmentTypes';

// ─── Types ────────────────────────────────────────────────────────────────────

export type PreviewFormat =
  | 'carousel'
  | 'feed_1x1'
  | 'story_9x16'
  | 'pinterest_standard_pin'
  | 'twitter_promoted'
  | 'google_display'
  | 'google_search';

const FORMAT_LABEL: Record<PreviewFormat, string> = {
  carousel: 'Meta Carousel',
  feed_1x1: 'Meta Feed',
  story_9x16: 'Meta Story',
  pinterest_standard_pin: 'Pinterest',
  twitter_promoted: 'X Promoted',
  google_display: 'Google Display',
  google_search: 'Google Search',
};

const FORMAT_PLATFORM: Record<PreviewFormat, PlatformKey> = {
  carousel: 'meta',
  feed_1x1: 'meta',
  story_9x16: 'meta',
  pinterest_standard_pin: 'pinterest',
  twitter_promoted: 'twitter',
  google_display: 'google_ads',
  google_search: 'google_ads',
};

/** Output pixel dimensions for each format — used by image_processor.py at publish time. */
const FORMAT_OUTPUT: Partial<Record<PreviewFormat, { w: number; h: number }>> = {
  feed_1x1: { w: 1080, h: 1350 },
  story_9x16: { w: 1080, h: 1920 },
  pinterest_standard_pin: { w: 1000, h: 1500 },
  // X's in-feed link ad is 1.91:1 — the website card spec.
  twitter_promoted: { w: 1200, h: 628 },
  google_display: { w: 1200, h: 628 },
};

export interface AdPreviewModalProps {
  state: CampaignState;
  concept: CreativeConcept;
  initialFormat: PreviewFormat;
  conceptIndex: number;
  uploadedImages?: Array<{ url: string }>;
  onAdjustmentSave?: (format: PreviewFormat, crop: CropParams, targetW: number, targetH: number) => void;
  onClose: () => void;
}

// ─── Lightweight platform chrome shells ───────────────────────────────────────

/** True while previewing a finished ad: show the file whole, no pan/crop. */
const FinishedAd = createContext(false);

/** The image slot: pannable for plain photos, the whole file for finished ads. */
const Media: React.FC<React.ComponentProps<typeof PannableImage>> = (props) => {
  const finished = useContext(FinishedAd);
  if (!finished) return <PannableImage {...props} />;
  return <img src={props.src} alt="" className="h-full w-full object-contain bg-gray-100 dark:bg-gray-800" />;
};

/** Feed frame: 4:5 for plain photos; a finished ad keeps its own shape. */
const FeedFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const finished = useContext(FinishedAd);
  return <div className={finished ? 'relative [&>img]:h-auto' : 'relative aspect-[4/5]'}>{children}</div>;
};

// Preview tab -> the rendition format whose file it shows.
const RENDITION_OF: Partial<Record<string, string>> = {
  feed_1x1: 'feed_1x1', story_9x16: 'story_9x16', pinterest_standard_pin: 'standard_pin',
  twitter_promoted: 'promoted_1_91x1', google_display: 'display',
};

const FeedShell: React.FC<{
  brandName: string;
  headline: string;
  body: string;
  mediaUrl?: string;
  crop?: CropParams;
  onCropChange: (c: CropParams) => void;
}> = ({ brandName, headline, body, mediaUrl, crop, onCropChange }) => (
  <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-900 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white">
    {/* Header */}
    <div className="flex items-center gap-2.5 px-3 py-2.5">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#1877F2] text-[11px] font-bold text-white">
        {brandName.slice(0, 2).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold leading-tight">{brandName}</p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400">Sponsored · 🌐</p>
      </div>
      <span className="text-gray-400 text-lg leading-none">···</span>
    </div>

    {/* Body text */}
    {body && <p className="px-3 pb-2 text-[13px] leading-snug">{body}</p>}

    {/* Image — 4:5, pannable (a finished ad: whole, its own shape) */}
    <FeedFrame>
      {mediaUrl ? (
        <Media
          src={mediaUrl}
          outputWidth={1080}
          outputHeight={1350}
          existingCrop={crop}
          onCropChange={onCropChange}
        />
      ) : (
        <div className="aspect-[4/5] w-full bg-[#1877F2]/10" />
      )}
    </FeedFrame>

    {/* Link card */}
    <div className="flex items-center justify-between bg-gray-100 px-3 py-2 dark:bg-gray-800">
      <div className="min-w-0 flex-1 pr-2">
        <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">yourbrand.com</p>
        <p className="truncate text-[13px] font-semibold">{headline}</p>
      </div>
      <button className="flex-shrink-0 rounded bg-gray-200 px-3 py-1.5 text-[12px] font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-200">
        Shop Now
      </button>
    </div>

    {/* Engagement */}
    <div className="flex border-t border-gray-200 dark:border-gray-700">
      {['👍 Like', '💬 Comment', '↗ Share'].map((a) => (
        <button
          key={a}
          className="flex-1 py-2 text-center text-[12px] font-medium text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          {a}
        </button>
      ))}
    </div>
  </div>
);

const StoryShell: React.FC<{
  brandName: string;
  headline: string;
  mediaUrl?: string;
  crop?: CropParams;
  onCropChange: (c: CropParams) => void;
}> = ({ brandName, headline, mediaUrl, crop, onCropChange }) => (
  <div className="mx-auto w-full max-w-[380px]">
    <div className="relative aspect-[9/16] overflow-hidden rounded-2xl bg-gray-900 shadow-xl">
      {/* Image — 9:16, pannable */}
      {mediaUrl ? (
        <Media
          src={mediaUrl}
          outputWidth={1080}
          outputHeight={1920}
          existingCrop={crop}
          onCropChange={onCropChange}
        />
      ) : (
        <div className="h-full w-full bg-gradient-to-br from-purple-700 via-pink-600 to-orange-400" />
      )}

      {/* Top chrome */}
      <div className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-black/50 to-transparent px-3 pb-4 pt-3">
        <div className="mb-2 flex gap-1">
          <div className="h-0.5 flex-1 rounded-full bg-white" />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-blue-400 text-[9px] font-bold text-white">
            {brandName.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <p className="text-[11px] font-semibold leading-none text-white">{brandName}</p>
            <p className="text-[9px] text-white/70">Sponsored</p>
          </div>
          <span className="ml-auto text-white/70 text-sm">···</span>
        </div>
      </div>

      {/* Bottom chrome */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-5 pt-8">
        <p className="mb-3 text-sm font-semibold leading-snug text-white">{headline}</p>
        <div className="flex items-center justify-center rounded-full bg-white py-2">
          <span className="text-xs font-semibold text-gray-900">Shop Now</span>
        </div>
      </div>
    </div>
  </div>
);

const TwitterShell: React.FC<{
  brandName: string;
  tweet: string;
  headline: string;
  mediaUrl?: string;
  crop?: CropParams;
  onCropChange: (c: CropParams) => void;
}> = ({ brandName, tweet, headline, mediaUrl, crop, onCropChange }) => (
  <div className="mx-auto w-full max-w-[420px] overflow-hidden rounded-2xl border-2 border-gray-200 bg-white shadow-md dark:border-gray-700 dark:bg-black">
    <div className="flex gap-3 p-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[11px] font-bold text-gray-700 dark:bg-gray-700 dark:text-gray-300">
        {brandName.slice(0, 2).toUpperCase()}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 text-[13px]">
          <span className="font-bold text-gray-900 dark:text-white">{brandName}</span>
          <svg className="h-3.5 w-3.5 text-[#1D9BF0]" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81C14.67 2.63 13.43 1.75 12 1.75s-2.67.88-3.34 2.19c-1.39-.46-2.9-.2-3.91.81s-1.27 2.52-.81 3.91C2.63 9.33 1.75 10.57 1.75 12s.88 2.67 2.19 3.34c-.46 1.39-.2 2.9.81 3.91s2.52 1.27 3.91.81c.67 1.31 1.91 2.19 3.34 2.19s2.67-.88 3.34-2.19c1.39.46 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.67 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42 2.26 2.26 4.8-5.23 1.47 1.36-6.2 6.77z" />
          </svg>
          <span className="truncate text-gray-500 dark:text-gray-400">
            @{brandName.toLowerCase().replace(/\s+/g, '')}
          </span>
        </div>

        <p className="mt-1 whitespace-pre-wrap text-[14px] leading-snug text-gray-900 dark:text-white line-clamp-4">
          {tweet}
        </p>

        {/* Website card: 1.91:1 image with the headline strip beneath */}
        <div className="mt-2.5 overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700">
          <div className="relative aspect-[1.91/1]">
            {mediaUrl ? (
              <Media
                src={mediaUrl}
                outputWidth={1200}
                outputHeight={628}
                existingCrop={crop}
                onCropChange={onCropChange}
              />
            ) : (
              <div className="h-full w-full bg-gradient-to-br from-sky-100 via-slate-50 to-slate-100 dark:from-gray-800 dark:to-gray-700" />
            )}
          </div>
          <div className="bg-gray-50 px-3 py-2 dark:bg-gray-900">
            <p className="truncate text-[13px] font-medium text-gray-900 dark:text-white">
              {headline}
            </p>
          </div>
        </div>

        <p className="mt-2 text-[12px] text-gray-500 dark:text-gray-400">Promoted</p>
      </div>
    </div>
  </div>
);

const PinterestShell: React.FC<{
  brandName: string;
  headline: string;
  body: string;
  mediaUrl?: string;
  crop?: CropParams;
  onCropChange: (c: CropParams) => void;
}> = ({ brandName, headline, body, mediaUrl, crop, onCropChange }) => (
  <div className="mx-auto w-full max-w-[560px] overflow-hidden rounded-2xl border-2 border-gray-200 bg-white shadow-md dark:border-gray-700 dark:bg-gray-900">
    <div className="relative aspect-[2/3]">
      {mediaUrl ? (
        <Media
          src={mediaUrl}
          outputWidth={1000}
          outputHeight={1500}
          existingCrop={crop}
          onCropChange={onCropChange}
        />
      ) : (
        <div className="h-full w-full bg-gradient-to-br from-pink-100 via-red-50 to-orange-100 dark:from-gray-800 dark:to-gray-700" />
      )}

      {/* Promoted badge */}
      <div className="pointer-events-none absolute left-3 top-3">
        <span className="rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold text-gray-700 shadow-sm backdrop-blur-sm">
          Promoted
        </span>
      </div>

      {/* Save button */}
      <div className="pointer-events-none absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-[#E60023] shadow-lg">
        <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        </svg>
      </div>
    </div>

    {/* Pin info */}
    <div className="p-3">
      <p className="text-[13px] font-bold leading-snug text-gray-900 dark:text-white line-clamp-2">{headline}</p>
      {body && <p className="mt-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400 line-clamp-2">{body}</p>}
      <div className="mt-2.5 flex items-center gap-2">
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-[9px] font-bold text-gray-700 dark:bg-gray-700 dark:text-gray-300">
          {brandName.slice(0, 2).toUpperCase()}
        </div>
        <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300">{brandName}</span>
        <button className="ml-auto rounded-full border border-gray-300 px-2.5 py-0.5 text-[11px] font-semibold text-gray-700 dark:border-gray-600 dark:text-gray-300">
          Follow
        </button>
      </div>
    </div>
  </div>
);

const DisplayShell: React.FC<{
  brandName: string;
  headline: string;
  body: string;
  mediaUrl?: string;
  crop?: CropParams;
  onCropChange: (c: CropParams) => void;
}> = ({ brandName, headline, body, mediaUrl, crop, onCropChange }) => (
  <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
    {/* Image — 16:9, pannable */}
    <div className="relative aspect-video">
      {mediaUrl ? (
        <Media
          src={mediaUrl}
          outputWidth={1200}
          outputHeight={628}
          existingCrop={crop}
          onCropChange={onCropChange}
        />
      ) : (
        <div className="h-full w-full bg-gray-200 dark:bg-gray-700" />
      )}
      {/* Ad badge */}
      <div className="pointer-events-none absolute left-2 top-2">
        <span className="rounded border border-gray-300 bg-white/90 px-1 py-0.5 text-[10px] font-medium text-gray-600 backdrop-blur-sm">
          Ad
        </span>
      </div>
    </div>

    {/* Content */}
    <div className="flex items-center gap-3 px-3 py-2.5">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#4285F4] text-[10px] font-bold text-white">
        {brandName.slice(0, 2).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold text-gray-900 dark:text-white">{headline}</p>
        {body && <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">{body}</p>}
      </div>
      <button className="flex-shrink-0 rounded border border-[#1a73e8] px-2.5 py-1 text-[11px] font-semibold text-[#1a73e8] hover:bg-blue-50">
        Learn more
      </button>
    </div>
  </div>
);

const CarouselShell: React.FC<{
  brandName: string;
  body: string;
  cards: Array<{ id?: string; assetUrl?: string; headline?: string }>;
}> = ({ brandName, body, cards }) => (
  <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-900 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white">
    {/* Header */}
    <div className="flex items-center gap-2.5 px-3 py-2.5">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#1877F2] text-[11px] font-bold text-white">
        {brandName.slice(0, 2).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold leading-tight">{brandName}</p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400">Sponsored · 🌐</p>
      </div>
      <span className="text-lg leading-none text-gray-400">···</span>
    </div>
    {body && <p className="px-3 pb-2 text-[13px] leading-snug">{body}</p>}
    {/* Cards — square (1:1), horizontally swipeable, like Meta's carousel */}
    <div className="flex snap-x gap-2 overflow-x-auto px-3 pb-3">
      {cards.map((c, i) => (
        <div
          key={c.id ?? i}
          className="w-[220px] flex-shrink-0 snap-start overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
        >
          <div className="relative aspect-square bg-gray-100 dark:bg-gray-800">
            {c.assetUrl && <img src={c.assetUrl} alt="" className="h-full w-full object-cover" />}
          </div>
          <div className="flex items-center justify-between gap-2 bg-gray-100 px-2.5 py-2 dark:bg-gray-800">
            <p className="truncate text-[12px] font-semibold">{c.headline || brandName}</p>
            <button className="flex-shrink-0 rounded bg-gray-200 px-2 py-1 text-[11px] font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-200">
              Shop
            </button>
          </div>
        </div>
      ))}
    </div>
    {/* Engagement */}
    <div className="flex border-t border-gray-200 dark:border-gray-700">
      {['👍 Like', '💬 Comment', '↗ Share'].map((a) => (
        <button
          key={a}
          className="flex-1 py-2 text-center text-[12px] font-medium text-gray-600 dark:text-gray-400"
        >
          {a}
        </button>
      ))}
    </div>
  </div>
);

// ─── Modal ────────────────────────────────────────────────────────────────────

export const AdPreviewModal: React.FC<AdPreviewModalProps> = ({
  state,
  concept,
  initialFormat,
  conceptIndex,
  uploadedImages,
  onAdjustmentSave,
  onClose,
}) => {
  const [activeFormat, setActiveFormat] = useState<PreviewFormat>(initialFormat);
  // Per-format crop state — initialised from saved adjustments
  const [crops, setCrops] = useState<Partial<Record<PreviewFormat, CropParams>>>(() => {
    const saved: Partial<Record<PreviewFormat, CropParams>> = {};
    if (concept.adjustments) {
      for (const [fmt, adj] of Object.entries(concept.adjustments)) {
        if (adj?.crop) saved[fmt as PreviewFormat] = adj.crop as CropParams;
      }
    }
    return saved;
  });
  const [dirty, setDirty] = useState<Set<PreviewFormat>>(new Set());

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const mediaUrl =
    conceptAssetUrl(concept) ??
    uploadedImages?.[conceptIndex % (uploadedImages?.length ?? 1)]?.url;

  const sharedMeta = state.messaging?.meta ?? {};
  const google = state.messaging?.google ?? {};
  const brandName = (state.name as string) ?? 'Your Brand';

  // Per-concept copy takes precedence over shared messaging — same priority as publishService.ts.
  const conceptCopy = concept.copy as any;
  const headline =
    conceptCopy?.meta?.headline ??
    sharedMeta.headline ??
    brandName;
  const body =
    conceptCopy?.meta?.body ??
    sharedMeta.body ?? '';

  // Pinterest-specific copy, falling back to meta then shared.
  const pinterestHeadline =
    conceptCopy?.pinterest?.title ??
    conceptCopy?.meta?.headline ??
    sharedMeta.headline ??
    brandName;
  const pinterestBody =
    conceptCopy?.pinterest?.description ??
    conceptCopy?.meta?.body ??
    sharedMeta.body ?? '';

  // Which tabs to show — mirrors CreativePreviews visibility logic
  const renditions = concept.renditions ?? [];
  const fmtEnabled = (fmt: string) => renditions.some((r) => r.format === fmt && r.enabled !== false);
  const hasGoogle = renditions.some((r) => (r.platform === 'google' || r.platform === 'google_ads') && r.enabled !== false);
  const strategyPlatforms = getStrategyPlatforms(state);
  const inStrategy = (p: string) => strategyPlatforms.size === 0 || strategyPlatforms.has(p);
  const hasPinterest = renditions.some((r) => r.platform === 'pinterest' && r.enabled !== false) && inStrategy('pinterest');
  const hasTwitter =
    renditions.some(
      (r) => (r.platform === 'twitter' || r.platform === 'x') && r.enabled !== false,
    ) && inStrategy('twitter');

  // X copy, falling back to meta then shared.
  const twitterTweet =
    conceptCopy?.twitter?.tweet ??
    state.messaging?.twitter?.tweet ??
    conceptCopy?.meta?.body ??
    sharedMeta.body ??
    brandName;
  const twitterHeadline =
    conceptCopy?.twitter?.headline ??
    conceptCopy?.meta?.headline ??
    sharedMeta.headline ??
    brandName;

  const isCarousel = (concept.cards?.length ?? 0) >= 2 && inStrategy('meta');
  const tabs: PreviewFormat[] = (
    [
      isCarousel && 'carousel',
      fmtEnabled('feed_1x1') && 'feed_1x1',
      fmtEnabled('story_9x16') && 'story_9x16',
      hasPinterest && 'pinterest_standard_pin',
      hasTwitter && 'twitter_promoted',
      hasGoogle && 'google_display',
      hasGoogle && (google.headlines?.length ?? 0) > 0 && 'google_search',
    ] as Array<PreviewFormat | false>
  ).filter(Boolean) as PreviewFormat[];

  const handleCropChange = useCallback((format: PreviewFormat, crop: CropParams) => {
    setCrops((prev) => ({ ...prev, [format]: crop }));
    setDirty((prev) => new Set([...prev, format]));
  }, []);

  const handleSave = useCallback(() => {
    if (!onAdjustmentSave) return;
    for (const fmt of dirty) {
      const crop = crops[fmt];
      const dims = FORMAT_OUTPUT[fmt];
      if (crop && dims) onAdjustmentSave(fmt, crop, dims.w, dims.h);
    }
    setDirty(new Set());
  }, [dirty, crops, onAdjustmentSave]);

  const finished = concept.bakedCopy === true;
  // a finished ad shows each format's own file; plain photos share the master
  const formatUrl = (fmt: PreviewFormat) =>
    (finished && renditions.find((r) => r.format === RENDITION_OF[fmt] && r.assetUrl)?.assetUrl) || mediaUrl;

  const renderFormat = () => {
    const crop = crops[activeFormat];
    const mediaUrl = formatUrl(activeFormat);
    const onCropChange = (c: CropParams) => handleCropChange(activeFormat, c);

    switch (activeFormat) {
      case 'carousel':
        return <CarouselShell brandName={brandName} body={body} cards={concept.cards ?? []} />;
      case 'feed_1x1':
        return (
          <FeedShell
            brandName={brandName}
            headline={headline}
            body={body}
            mediaUrl={mediaUrl}
            crop={crop}
            onCropChange={onCropChange}
          />
        );
      case 'story_9x16':
        return (
          <StoryShell
            brandName={brandName}
            headline={headline}
            mediaUrl={mediaUrl}
            crop={crop}
            onCropChange={onCropChange}
          />
        );
      case 'pinterest_standard_pin':
        return (
          <PinterestShell
            brandName={brandName}
            headline={pinterestHeadline}
            body={pinterestBody}
            mediaUrl={mediaUrl}
            crop={crop}
            onCropChange={onCropChange}
          />
        );
      case 'twitter_promoted':
        return (
          <TwitterShell
            brandName={brandName}
            tweet={twitterTweet}
            headline={twitterHeadline}
            mediaUrl={mediaUrl}
            crop={crop}
            onCropChange={onCropChange}
          />
        );
      case 'google_display':
        return (
          <DisplayShell
            brandName={brandName}
            headline={google.headlines?.[0] ?? headline}
            body={google.descriptions?.[0] ?? body}
            mediaUrl={mediaUrl}
            crop={crop}
            onCropChange={onCropChange}
          />
        );
      case 'google_search':
        return (
          <GoogleSearchAdMockup
            headlines={google.headlines?.length ? google.headlines : [headline]}
            descriptions={google.descriptions?.length ? google.descriptions : body ? [body] : []}
            finalUrl="yourbrand.com"
          />
        );
      default:
        return null;
    }
  };

  const hasImage = FORMAT_OUTPUT[activeFormat] != null;

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
        style={{ maxHeight: 'calc(100vh - 48px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Tab bar + close — fixed */}
        <div className="flex flex-shrink-0 items-center gap-2 border-b border-border px-4 py-3">
          <div className="flex flex-1 flex-wrap items-center gap-1">
            {tabs.map((fmt) => (
              <button
                key={fmt}
                onClick={() => setActiveFormat(fmt)}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  activeFormat === fmt
                    ? 'bg-primary text-white'
                    : 'text-ink-3 hover:bg-surface hover:text-ink',
                  dirty.has(fmt) && activeFormat !== fmt && 'ring-1 ring-primary/40',
                )}
              >
                <PlatformLogo platform={FORMAT_PLATFORM[fmt]} size="2xs" />
                {FORMAT_LABEL[fmt]}
                {dirty.has(fmt) && ' ·'}
              </button>
            ))}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 rounded-lg p-1.5 text-ink-3 transition-colors hover:bg-surface hover:text-ink"
            aria-label="Close preview"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mockup — scrollable */}
        <div className="flex-1 overflow-y-auto p-5">
          <FinishedAd.Provider value={finished}>{renderFormat()}</FinishedAd.Provider>
        </div>

        {/* Footer — fixed */}
        {hasImage && onAdjustmentSave && !finished && (
          <div className="flex flex-shrink-0 items-center justify-between border-t border-border px-4 py-3">
            <p className="text-xs text-ink-3">
              {dirty.size > 0
                ? `${dirty.size} format${dirty.size > 1 ? 's' : ''} repositioned — unsaved`
                : 'Drag image to reposition within the frame'}
            </p>
            <button
              onClick={handleSave}
              disabled={dirty.size === 0}
              className={cn(
                'rounded-lg px-4 py-1.5 text-xs font-semibold transition-colors',
                dirty.size > 0
                  ? 'bg-primary text-white hover:bg-primary/90'
                  : 'cursor-default text-ink-3',
              )}
            >
              Save position
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};
